import React, { createRef, useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import cx from 'classnames';
import DrawIcon from '@mui/icons-material/DrawOutlined';
import { ReactComponent as SendIcon } from '../../../../assets/icons/sendClipsIcon.svg';
import { useDrawingMarkers } from '../../../VideoDrawing/context';

import Avatar from '../../Avatar';
import { ReactComponent as ArrowIcon } from '../../../../assets/icons/comment-arrow.svg';
import { ReactComponent as ArrowUpIcon } from '../../../../assets/icons/send.svg';
import styles from './Comment.module.scss';
import Footer from '../CommentFooter';
import { COMMENT } from '../../../../enums/TYPES';
import TrashButton from '../../TrashButton';
import { useAppSelector } from '../../../../store/hooks';
import { TUser } from '../../../../store/user/userSlice';
import Reply from '../Reply';
import { DateFormatter } from '../../../../helpers/dateFormatter';
import { formatRelativeTime, timeLineFormatTime } from '../../../../helpers';
import CrossIcon from '../../../../assets/icons/cross.svg';
import EditButton from '../../EditButton';
import { MoreIcon } from '../../../../assets/icons/threedots';
import { useOnClickOutside } from 'usehooks-ts';
import { findSearchValue } from '../../../../helpers';
import { ROLES } from '../../../../enums/ROLES';
import { getActiveReplies, getCommentDate, compareCommentsByTime, isVisibleComment } from '../../../../helpers/comments';

type TComment = {
  comment: COMMENT;
  small?: boolean;
  disable: boolean;
  videofeedId: string;
  fetch: (value: string) => void;
  onDelete: (comment: COMMENT) => any;
  onEditSubmit?: (comment: COMMENT, newMessage: string) => void;
  autoExpand?: boolean;
  autoEdit?: boolean;
  revealSequence?: number;
  onCreateClip?: (comment: COMMENT) => void;
};

const Comment: React.FC<TComment> = ({ comment, small, videofeedId, fetch, onDelete, onEditSubmit, disable, autoExpand, autoEdit, revealSequence, onCreateClip }) => {
  const { showMarker, markers, consumeDiscussionEdit, finishDiscussionEdit } = useDrawingMarkers();
  const deleted = !!comment.deleted_at;
  const replies = getActiveReplies(comment).sort(compareCommentsByTime);
  const [showFooter, setShowFooter] = useState(false);
  const notification = useSnackbar();

  const [isEdit, setIsEdit] = useState(false);
  const [newMsg, setNewMsg] = useState('');
  const editInputRef = useRef<HTMLTextAreaElement>(null);
  const automaticEditSequence = useRef<number>();
  const [disableDelete, setDisableDelete] = useState(false);
  const [showDotsMenu, setShowDotsMenu] = useState(false);
  const actionMenuRef = useRef<HTMLDivElement | null>(null);
  const { currentUser, role } = useAppSelector((state: { user: TUser }) => state.user);
  const { videoPlayedSeconds } = useAppSelector((state) => state.videoStore);
  const showDeleteBtn = !deleted && (
    currentUser?.id === comment?.created_by?.id ||
    (findSearchValue('allContent') === 'true' && role === ROLES.ORGANIZATION_ADMIN)
  );
  const showEditBtn = !deleted && (comment.drawing
    ? markers.find((marker) => String(marker.id) === String(comment.id))?.can_edit_drawing === true
    : showDeleteBtn);
  const canCreateClip = !!onCreateClip && !deleted && comment.parent_type === 'videofeed' && Number.isFinite(comment.timestamp);
  const [imperativlyShowFooter, setImpertivlyShowFooter] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const footerRef = createRef<HTMLDivElement>();

  const repliesArray = replies;

  const onExpand = () => {
    setExpanded(true);
  };

  const onHide = () => {
    onHighlightReplyClose();
    setExpanded(false);
    setImpertivlyShowFooter(false);
  };

  const handleCommentDelete = async () => {
    if (disableDelete) {
      setShowDotsMenu(false);
      return;
    }
    try {
      setDisableDelete(true);
      await onDelete(comment);
    } catch {
      // The caller reports deletion errors.
    } finally {
      setDisableDelete(false);
      setShowDotsMenu(false);
    }
  };

  const switchDotsMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    setShowDotsMenu(true);
  };

  const focusOnInput = () => {
    if (footerRef && footerRef.current) {
      const inputRef = footerRef.current.querySelector('textarea');
      if (inputRef) {
        inputRef.focus();
      }
    }
  };

  useOnClickOutside(actionMenuRef, (e) => {
    e.stopPropagation();
    setShowDotsMenu(false);
  });

  const onEditClose = (resume = false) => {
    if (automaticEditSequence.current != null) {
      finishDiscussionEdit(automaticEditSequence.current, resume);
      automaticEditSequence.current = undefined;
    }
    setIsEdit(false);
    setNewMsg('');
  };

  const handleReplyDelete = (reply: COMMENT) => {
    setShowDotsMenu(false);
    onDelete(reply);
  };

  const onMsgEdit = (e: any) => {
    setNewMsg(e.target.value);
  };

  const onMsgSubmit = (e: any) => {
    if (!newMsg.trim() && !comment.drawing) {
      notification.showMessage('Empty message');
      return;
    }
    if (comment.message.trim() !== newMsg.trim() && onEditSubmit) onEditSubmit(comment, newMsg);

    onEditClose(!newMsg.trim());
  };

  const handleEdit = () => {
    setShowDotsMenu(false);
    setNewMsg(comment.message);
    setIsEdit(!isEdit);
  };

  const closeFooter = () => {
    setShowFooter(false);
    onHighlightReplyClose();
  };

  const [highlightReplies, setHighlightReplies] = useState(false);
  const onHighlightReply = () => {
    setHighlightReplies(true);
  };
  const onHighlightReplyClose = () => {
    setHighlightReplies(false);
  };
  const commentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!autoExpand) return;
    if (replies.length) setExpanded(true);
    commentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    const card = commentRef.current?.querySelector(`.${styles.comment}`);
    const shadow = card ? getComputedStyle(card).boxShadow : 'none';
    const glow = { boxShadow: '0 0 12px rgba(167, 112, 232, .4), inset 0 0 0 1px rgba(167, 112, 232, .35)' };
    const animation = card?.animate?.([
      { boxShadow: shadow, offset: 0 }, { ...glow, offset: .1 }, { ...glow, offset: .75 },
      { boxShadow: shadow },
    ], { duration: 2000, easing: 'ease-out' });
    return () => animation?.cancel();
  }, [autoExpand, replies.length, revealSequence]);

  useEffect(() => {
    if (!autoEdit || !showEditBtn || revealSequence == null || !consumeDiscussionEdit(revealSequence)) return;
    automaticEditSequence.current = revealSequence;
    setNewMsg(comment.message);
    setIsEdit(true);
  }, [autoEdit, revealSequence, showEditBtn, consumeDiscussionEdit]);

  useEffect(() => { if (isEdit) editInputRef.current?.focus({ preventScroll: true }); }, [isEdit]);

  useEffect(() => {
    if (deleted) {
      setIsEdit(false);
      setNewMsg('');
      setShowDotsMenu(false);
      setShowFooter(false);
      setImpertivlyShowFooter(false);
    }
  }, [deleted]);

  if (!isVisibleComment(comment)) return null;

  return (
    <div className={styles.customShadow} ref={commentRef}>
      <div className={cx({ [styles.commentWrapper]: true, [styles.commentSmall]: small })}>
        {comment ? (
          <div className={cx(styles.comment, deleted && styles.deletedComment)}>
            <div className={styles.commentHeader}>
              <div className={styles.user}>
                <Avatar
                  avatar={comment?.created_by?.avatar_url}
                  className={cx(styles.avatar, { [styles.avatarSmall]: small })}
                  name={comment?.created_by?.username?.toLocaleLowerCase()}
                />

                <div className={styles.userName}>
                  <div className={styles.name}>{comment?.created_by?.username}</div>
                </div>
              </div>

              <div className={styles.actionBtns}>
                {(showDeleteBtn || showEditBtn || canCreateClip) && (
                  <button type="button" className={styles.dotsMenuBtn} onClick={switchDotsMenu} disabled={isEdit} aria-label="Comment actions">
                    <div className={styles.dotIcon}>
                      <MoreIcon />
                    </div>
                  </button>
                )}
              </div>
            </div>
            {!deleted && comment.drawing && <button type="button" className={styles.drawingComment} onClick={() => showMarker(comment)}>
              <DrawIcon /> Drawing · {timeLineFormatTime(comment.timestamp)}
            </button>}
            {!deleted && comment.quoted_parent_text && <blockquote className={styles.quotedText}>{comment.quoted_parent_text}</blockquote>}
            {isEdit && !deleted ? (
              <div className={cx(styles.editTextBlock, styles.mainCommentEditTextWrapper)}>
                <div ref={footerRef} className={styles.editTextWrapper}>
                  <textarea ref={editInputRef} aria-label="Edit comment text" className={styles.editText} onChange={onMsgEdit} value={newMsg}
                    rows={Math.min(6, Math.max(2, newMsg.split('\n').length))}
                    onKeyDown={(event) => {
                      if (event.key === ' ' && !newMsg.trim() && automaticEditSequence.current != null) {
                        event.preventDefault(); event.stopPropagation(); onMsgSubmit(event);
                      }
                      if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); event.stopPropagation(); onMsgSubmit(event); }
                      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); onEditClose(!newMsg.trim()); }
                    }} maxLength={comment.drawing ? 255 : undefined} />
                  <button onClick={focusOnInput} type="submit" className={styles.footerButton}>
                    <ArrowUpIcon onClick={onMsgSubmit} />
                  </button>
                </div>
                <img className={styles.removeChip} src={CrossIcon} alt="close" onClick={() => onEditClose()} />
              </div>
            ) : (
              <div className={styles.commentText} onClick={!deleted && comment.drawing ? () => showMarker(comment) : undefined}
                style={!deleted && comment.drawing ? { cursor: 'pointer' } : undefined}>{deleted ? 'Comment deleted' : comment?.message}</div>
            )}
            <div className={styles.date}>
              <div>{DateFormatter(getCommentDate(comment), true)}</div>
            </div>

            {!deleted && (showFooter ? (
              <div className={styles.replyInputContainer}>
                <Footer
                  showFooter={showFooter}
                  commentData={comment}
                  commentId={comment.id}
                  videofeedId={videofeedId}
                  fetch={fetch}
                  reply
                  setShowFooter={setShowFooter}
                  videoPlayedSeconds={videoPlayedSeconds}
                  small={small}
                />
                <img className={styles.removeChip} src={CrossIcon} alt="close" onClick={closeFooter} />
              </div>
            ) : (
              <div className={styles.replyBtn}>
                <button
                  type="button"
                  disabled={disable}
                  className={disable ? styles.commentButtonDisabled : styles.commentButton}
                  onClick={() => setShowFooter(!showFooter)}
                >
                  <ArrowIcon />
                </button>
              </div>
            ))}

            <div className={styles.buttonWrapper}>
              {!small &&
                replies.length > 0 &&
                (expanded ? (
                  <div className={styles.hideBtn} onClick={onHide}>
                    hide replies
                  </div>
                ) : (
                  <div className={styles.repliesMini}>
                    <div className={styles.expand} onClick={onExpand}>
                      {replies.slice(0, 3).map((reply) => (
                        <div key={reply.id}>
                          <Avatar
                            name={reply.created_by?.username?.toLocaleLowerCase()}
                            avatar={reply.created_by?.avatar_url}
                            className={styles.previewAvatarSmall}
                          />
                        </div>
                      ))}
                    </div>
                    <div className={styles.expandBtn} onClick={onExpand}>
                      {replies.length} {replies.length > 1 ? 'replies' : 'reply'}{' '}
                      <span style={{ marginLeft: '4px' }}>{formatRelativeTime(new Date(getCommentDate(replies[replies.length - 1])))}</span>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        ) : (
          <></>
        )}

        <div
          ref={actionMenuRef}
          className={cx(styles.actionMenu, canCreateClip && styles.commentClipMenu)}
          style={{
            position: 'absolute',
            display: showDotsMenu && !deleted ? 'block' : 'none',
            top: `55px`,
            right: `15px`,
          }}
        >
          {showEditBtn &&
            <div className={styles.menuOption} onClick={handleEdit}>
              <EditButton /> Edit
            </div>}
          {showDeleteBtn &&
            <div className={styles.menuOption} onClick={handleCommentDelete}>
              <TrashButton /> Delete
            </div>}
          {canCreateClip && <button type="button" className={cx(styles.menuOption, styles.createClipMenuItem)} onClick={() => {
            setShowDotsMenu(false);
            onCreateClip?.(comment);
          }}><SendIcon /> Create clip and send to Composer</button>}
        </div>
      </div>
      {expanded &&
        repliesArray.length > 0 &&
        repliesArray.map((reply, i) => (
            <Reply
              videofeedId={videofeedId}
              fetch={fetch}
              comment={reply}
              key={reply.id}
              onDelete={() => handleReplyDelete(reply)}
              onEditSubmit={onEditSubmit}
              disable={disable}
              parentDeleted={deleted}
              lastReply={i === repliesArray.length - 1}
              setImpertivlyShowFooter={setImpertivlyShowFooter}
              imperativlyShowFooter={imperativlyShowFooter}
              highlightReplies={highlightReplies}
            />
          ))}

      {repliesArray.length > 0 && expanded && (
        <div onClick={onHide} onMouseEnter={onHighlightReply} onMouseLeave={onHighlightReplyClose} className={styles.hideThread}>
          {' '}
          Hide Replies{' '}
        </div>
      )}
    </div>
  );
};

export default Comment;
